import { nameAccessorials } from "@/Data/nameAccessorials";
import { formatHandlingUnitTypeWithQuantity } from "@/Data/formatHandlingUnitTypeWithQuantity";
import { describeLocationType } from "@/Data/describeLocationType";
import { dangerousGoodsDescribeHazardClass } from "@/Shared/DangerousGoods/describeHazardClass";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { quotesFormatLocation } from "../Helpers/quotesFormatLocation";
import { describeHours } from "@/Helpers/describeHours";
import { formatPhone } from "@/Helpers/formatPhone";
import {
  totalWeightForLineItem,
  totalWeightForLineItems,
} from "@/Helpers/lineItemCalculations";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { TemperatureHandling } from "@freightsimple/generated-dashboard-openapi-client/models/TemperatureHandling";
import { QuotesColumn } from "@/Shared/Quotes/CustomComponents/QuotesColumn";
import { QuotesColumnHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnHeader";
import { QuotesColumnSectionHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnSectionHeader";
import { QuotesLineItemText1 } from "@/Shared/Quotes/CustomComponents/QuotesLineItemText1";
import { QuotesLineItemText2 } from "@/Shared/Quotes/CustomComponents/QuotesLineItemText2";
import { QuotesLocationTypeDescription } from "@/Shared/Quotes/CustomComponents/QuotesLocationTypeDescription";
import { QuotesNotesSection } from "@/Shared/Quotes/CustomComponents/QuotesNotesSection";
import { QuotesPrimaryText } from "@/Shared/Quotes/CustomComponents/QuotesPrimaryText";
import { QuotesSecondaryText } from "@/Shared/Quotes/CustomComponents/QuotesSecondaryText";

interface PickupColumnProps {
  pickupDate: moment.Moment;
  pickupLocation: Location | AddLocationLocationType | PreQuoteLocation;
  pickupContact: Contact | undefined;
  lineItems: Array<LineItem | AddLineItemType>;
  shipmentState: ShipmentState;
  pickupReferenceNumber?: string | undefined;
  pickupBoothNumber?: string | undefined;
  notes?: string | undefined;
}

function WhenSection_(props: PickupColumnProps) {
  const { pickupLocation, pickupDate } = props;

  const formattedDate = pickupDate.format("dddd Do MMMM YYYY");
  const formattedHours = describeHours(pickupLocation.hours);
  const windowDescription = `Pickup window: ${formattedHours}`;

  return (
    <>
      <QuotesColumnSectionHeader>When</QuotesColumnSectionHeader>
      <QuotesPrimaryText>{formattedDate}</QuotesPrimaryText>
      <QuotesSecondaryText>{windowDescription}</QuotesSecondaryText>
    </>
  );
}

function WhereSection_(props: PickupColumnProps) {
  const { pickupLocation } = props;

  const locationTypeDescription = describeLocationType(
    pickupLocation.locationType,
    pickupLocation.distributionWarehouseBrand,
  );
  const formattedLocation = quotesFormatLocation(pickupLocation);

  if (pickupLocation.businessName === undefined) {
    return (
      <>
        <QuotesColumnSectionHeader>Where</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{formattedLocation}</QuotesPrimaryText>
        <QuotesSecondaryText>{locationTypeDescription}</QuotesSecondaryText>
      </>
    );
  } else {
    return (
      <>
        <QuotesColumnSectionHeader>Where</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{pickupLocation.businessName}</QuotesPrimaryText>
        <QuotesSecondaryText>
          Location Type:{" "}
          <QuotesLocationTypeDescription>
            {locationTypeDescription}
          </QuotesLocationTypeDescription>
        </QuotesSecondaryText>
        <QuotesSecondaryText>
          {pickupLocation.address.addressLine}
        </QuotesSecondaryText>
        <QuotesSecondaryText>
          {pickupLocation.address.addressLine2}
        </QuotesSecondaryText>
        <QuotesSecondaryText>{formattedLocation}</QuotesSecondaryText>
      </>
    );
  }
}

function ContactSection_(props: PickupColumnProps) {
  const { pickupContact } = props;

  if (pickupContact?.contactName === undefined) {
    return <></>;
  } else {
    return (
      <>
        <QuotesColumnSectionHeader>Contact</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{pickupContact?.contactName}</QuotesPrimaryText>
        <QuotesSecondaryText>{pickupContact?.emailAddress}</QuotesSecondaryText>
        <QuotesSecondaryText>
          {formatPhone(
            pickupContact.phoneNumber,
            pickupContact.phoneNumberExtension,
          )}
        </QuotesSecondaryText>
      </>
    );
  }
}

function SpecialServicesSection_(props: PickupColumnProps) {
  const { pickupLocation } = props;

  const lines = nameAccessorials(
    pickupLocation.accessorials,
    LocationContext.Pickup,
    pickupLocation.locationType,
  );

  return (
    <>
      <QuotesColumnSectionHeader>Special Services</QuotesColumnSectionHeader>
      <ul style={{ paddingInlineStart: "20px", marginBottom: "0" }}>
        {lines.map((line) => {
          return (
            <li key={line}>
              <QuotesSecondaryText>{line}</QuotesSecondaryText>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function PayloadSection_(props: PickupColumnProps) {
  const { lineItems } = props;

  const formattedLineItems = lineItems.map((li) => {
    const secondary = `${formatHandlingUnitTypeWithQuantity(
      li.numberHandlingUnits,
      li.handlingUnitType,
    )} ${li.description}`;

    let tertiary = `${li.length}" x ${li.width}" x ${li.height}"`;

    if (lineItems.length > 1) {
      tertiary += ` - ${totalWeightForLineItem(li)}lb`;
    }

    if (li.isStackable) {
      tertiary += ` - Stackable`;
    } else {
      tertiary += ` - Not Stackable`;
    }

    if (li.temperatureHandling === TemperatureHandling.ProtectFromFreezing) {
      tertiary += ` - Protect From Freezing`;
    }

    if (li.temperatureHandling === TemperatureHandling.KeepRefrigerated) {
      tertiary += ` - Keep Refrigerated`;
    }

    if (li.temperatureHandling === TemperatureHandling.KeepFrozen) {
      tertiary += ` - Keep Frozen`;
    }

    let dangerous = undefined;

    if (li.isDangerous && li.dangerousClassification) {
      dangerous = `Dangerous: ${
        li.dangerousUnNumber
      }, ${dangerousGoodsDescribeHazardClass(li.dangerousClassification)}`;
    }

    let nmfcItemNumber = undefined;

    if (li.nmfcItemNumber) {
      nmfcItemNumber = `NMFC: ${li.nmfcItemNumber}`;
    }

    return {
      secondary,
      tertiary,
      nmfcItemNumber,
      dangerous,
    };
  });

  return (
    <>
      <QuotesColumnSectionHeader>Payload</QuotesColumnSectionHeader>
      <QuotesPrimaryText>
        Total Weight: {totalWeightForLineItems(props.lineItems)}lb
      </QuotesPrimaryText>
      {formattedLineItems.map((li) => {
        return (
          <div
            className="flex flex-col items-start"
            key={`${li.secondary}-${li.tertiary}`}
            style={{ marginBottom: "4px" }}
          >
            <QuotesLineItemText1>{li.secondary}</QuotesLineItemText1>
            <QuotesLineItemText2>{li.tertiary}</QuotesLineItemText2>
            {li.nmfcItemNumber && (
              <QuotesLineItemText2>{li.nmfcItemNumber}</QuotesLineItemText2>
            )}
            {li.dangerous && (
              <QuotesLineItemText2>{li.dangerous}</QuotesLineItemText2>
            )}
          </div>
        );
      })}
      <div className="h-[4px]" />
    </>
  );
}

function QuotesPickupColumnForPickup(props: PickupColumnProps) {
  return (
    <QuotesColumn>
      <div className="flex flex-col items-start">
        <QuotesColumnHeader>Pickup</QuotesColumnHeader>
        <WhenSection_ {...props} />
        <WhereSection_ {...props} />
        <ContactSection_ {...props} />
        <PayloadSection_ {...props} />
        <SpecialServicesSection_ {...props} />
        <QuotesNotesSection
          reference={props.pickupReferenceNumber}
          boothNumber={props.pickupBoothNumber}
          locationType={props.pickupLocation.locationType}
          notes={props.pickupLocation.notes}
          onModify={undefined}
        />
      </div>
    </QuotesColumn>
  );
}
export default QuotesPickupColumnForPickup;
